
import React from 'react';
import { Button } from "@/components/ui/button";
import { ArrowUp } from "lucide-react";

const Privacy = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="container mx-auto px-4 py-16 max-w-4xl">
      <h1 className="text-3xl font-playfair font-bold mb-8 text-heaven-gold">Privacy Policy</h1>
      
      <div className="text-sm text-gray-600 mb-8">
        <p>Effective Date: July 21, 2024</p>
        <p>Last Updated: April 18, 2025</p>
      </div>

      <div className="prose max-w-none">
        <p className="mb-6">
          Your privacy matters. Selah Bible Tools is committed to protecting your trust and data. This Privacy Policy explains what information we collect, how we use it, and how we protect your experience on our platform.
        </p>

        <section id="data-collection" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">1. What We Collect — and What We Don't</h2>
          <p className="mb-4">
            Selah does not collect or store any personal information shared with AI tools. This includes:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>Prayer requests</li>
            <li>Personal struggles</li>
            <li>Theological questions</li>
            <li>Custom input for any Selah tool</li>
          </ul>
          <p>All input is processed in real time and is not stored, recorded, or tied to your identity.</p>
        </section>

        <section id="account-data" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">2. Account Data (If You Register)</h2>
          <p className="mb-4">If you create an account, we securely store:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Your name (optional)</li>
            <li>Email address</li>
            <li>Password (encrypted and never visible)</li>
          </ul>
          <p>
            This data is used only to personalize your experience and give you access to saved tools or progress (such as reading plans). We do not share or sell your information with any third parties.
          </p>
        </section>

        <section id="analytics" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">3. Analytics</h2>
          <p>
            Selah may use anonymous data (e.g., tool usage patterns) to improve platform performance. This data is not tied to any individual user and is used solely to understand what tools are being used and how often.
          </p>
        </section>

        <section id="cookies" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">4. Cookies & Session Info</h2>
          <p className="mb-4">We only use essential cookies to:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Maintain your login session</li>
            <li>Remember in-app preferences</li>
            <li>Load tools faster across visits</li>
          </ul>
          <p className="mb-4">We do not use:</p>
          <ul className="list-disc pl-6">
            <li>Advertising cookies</li>
            <li>Third-party marketing trackers</li>
            <li>Fingerprinting or behavioral profiling</li>
          </ul>
        </section>

        <section id="security" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">5. Security</h2>
          <p>
            We use industry-standard encryption (SSL) and secure cloud infrastructure to protect all data stored within the platform. Your login credentials and any associated account information are encrypted.
          </p>
        </section>

        <section id="rights" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">6. Your Rights</h2>
          <p>You have the right to:</p>
          <ul className="list-disc pl-6">
            <li>Use Selah completely anonymously (no account required)</li>
            <li>Delete your account at any time</li>
            <li>Request the deletion of your stored data</li>
            <li>Decline cookies or session storage preferences</li>
          </ul>
        </section>

        <section id="updates" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">7. Policy Updates</h2>
          <p>
            We may update this Privacy Policy to reflect changes in the platform. We will always post the latest version here and update the "Last Updated" date above. Continued use of Selah implies agreement with the latest version.
          </p>
        </section>

        <section id="contact" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">8. Contact Information</h2>
          <p>For questions, feedback, or privacy concerns, contact:</p>
          <p className="mt-2">
            📧 info@SelahTools.com<br />
            📍 MNP Enterprises LLC<br />
            Baltimore, Maryland, USA
          </p>
        </section>
      </div>

      <Button 
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 rounded-full p-3 bg-heaven-gold hover:bg-heaven-light-gold"
      >
        <ArrowUp className="h-5 w-5" />
      </Button>
    </div>
  );
};

export default Privacy;
